import {ChevronLeft, ChevronRight} from "lucide-react";
import {Button} from "@/components/ui/button";
import {cn} from "@/utils/utils";

type PageSize = 5 | 10 | 20 | 50;

type Props = {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onPrevPage: () => void;
    onNextPage: () => void;
    getPageNumbers: () => number[];
    className?: string;
};

const pageSizes: PageSize[] = [5, 10, 20, 50];

export function Pagination({
                               totalCount,
                               page,
                               pageSize,
                               totalPages,
                               onPageChange,
                               onPageSizeChange,
                               onPrevPage,
                               onNextPage,
                               getPageNumbers,
                               className,
                           }: Props) {
    if (!totalPages || totalPages <= 1) {
        return (
            <div
                className={cn(
                    "rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground",
                    className
                )}
            >
                Всего записей: <span className="text-foreground font-medium">{totalCount}</span>
            </div>
        );
    }

    return (
        <div
            className={cn(
                "rounded-lg border bg-card px-3 py-3 md:px-4",
                "flex flex-col gap-3 md:flex-row md:items-center md:justify-between",
                className
            )}
        >
            {/* Left info */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <div className="text-muted-foreground">
                    Всего: <span className="text-foreground font-medium">{totalCount}</span>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">На странице</span>
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        className={cn(
                            "h-9 rounded-md border border-input bg-background px-2 text-sm outline-none",
                            "focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                        )}
                    >
                        {pageSizes.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>

                    <span className="text-muted-foreground hidden sm:inline">
            Страниц: <span className="text-foreground font-medium">{totalPages}</span>
          </span>
                </div>
            </div>

            {/* Right controls */}
            <div className="flex items-center justify-between gap-2 md:justify-end">
                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={onPrevPage}
                        disabled={page === 1}
                        className="h-9 w-9"
                        aria-label="Предыдущая страница"
                    >
                        <ChevronLeft className="h-4 w-4"/>
                    </Button>

                    {/* Numbers */}
                    <div className="hidden sm:flex items-center gap-1">
                        {getPageNumbers().map((p) => {
                            const active = p === page;
                            return (
                                <Button
                                    key={p}
                                    variant={active ? "default" : "outline"}
                                    size="icon"
                                    onClick={() => onPageChange(p)}
                                    className={cn("h-9 w-9", active && "shadow-sm")}
                                    aria-label={`Страница ${p}`}
                                >
                                    {p}
                                </Button>
                            );
                        })}
                    </div>

                    {/* Mobile current */}
                    <div className="sm:hidden text-sm text-muted-foreground px-2">
                        <span className="text-foreground font-medium">{page}</span> / {totalPages}
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={onNextPage}
                        disabled={page === totalPages}
                        className="h-9 w-9"
                        aria-label="Следующая страница"
                    >
                        <ChevronRight className="h-4 w-4"/>
                    </Button>
                </div>
            </div>
        </div>
    );
}
